import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { attentionKeys, feedKeys, folderKeys, workFileKeys, workBoardKeys, filesOverviewKeys } from '~/lib/query-keys';
import { catalogueKeys } from './use-knowledge-catalogue';
import { knowledgeEnrichmentKeys } from './use-knowledge-enrichment';
import { knowledgeReviewKeys } from './use-knowledge-review';
import { knowledgeSearchKeys } from './use-knowledge-search';
import type { AttachedToContainer } from '~/hooks/mutations/use-document-mutations';
import { resolveUploadFiledMessage } from '~/lib/document-save-outcome';

export interface FileMeta {
  id: string;
  name: string;
  contentType: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  sizeBytes: number;
  kind: 'text' | 'image' | 'pdf' | 'binary';
  hasBytes: boolean;
  /** When the file was moved to the recycle bin (FRONT-594); null when live. */
  trashedAt?: string | null;
  /**
   * `ts_headline` excerpt from a CONTENT match on a command-palette search hit
   * (FRONT-873). Present (string or null) only on search results; absent on
   * the plain file list. Null when the row matched on name/description only.
   */
  snippet?: string | null;
}

export interface FileFull extends FileMeta {
  content: string;
  /** FRONT-2550: the upload's current tags (`okf_tags`); null/absent when none. */
  okfTags?: string[] | null;
  /** FRONT-2550: when Eddy tagged it; null if it did not or every tag was removed. */
  taggedByEddyAt?: string | null;
  createdBy?: string | null;
  zitadelOrgId?: string;
  userId?: string;
}

export const fileKeys = {
  all: ['files'] as const,
  list: () => [...fileKeys.all, 'list'] as const,
  trash: () => [...fileKeys.all, 'trash'] as const,
  detail: (id: string) => [...fileKeys.all, 'detail', id] as const,
  search: (query: string) => [...fileKeys.all, 'search', query] as const,
};

/**
 * `enabled` (FRONT-1085) defaults to `true` — unchanged for every existing
 * caller. `useUnifiedFiles`/the Work-container file picker pass
 * `enabled: isOpen` instead, so a hover-revealed picker doesn't fetch the
 * whole Files list on every mount — same convention as `useWorkBoard`.
 */
export function useFiles(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: fileKeys.list(),
    queryFn: async () => {
      const r = await api.get<{ items: FileMeta[] }>('/api/files');
      return r.items ?? [];
    },
    enabled: opts?.enabled ?? true,
  });
}

export function useFileDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: fileKeys.detail(id ?? ''),
    queryFn: () => api.get<FileFull>(`/api/files/${encodeURIComponent(id!)}`),
    enabled: !!id,
  });
}

/**
 * Search uploaded files by name/description (FRONT-872 — command palette
 * content search). Capped server-side at 5 hits. Disabled until the query is
 * non-empty so an idle search box makes no request.
 */
export function useFileSearch(query: string, limit = 5) {
  const q = query.trim();
  return useQuery({
    queryKey: [...fileKeys.search(q), limit],
    queryFn: async () => {
      const r = await api.get<{ items: FileMeta[] }>(
        `/api/files?q=${encodeURIComponent(q)}&limit=${limit}`,
      );
      return r.items ?? [];
    },
    enabled: q.length > 0,
  });
}

interface UpsertBody {
  name: string;
  content: string;
  contentType?: string;
  description?: string | null;
}

export function useCreateFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertBody) => api.post<FileFull>('/api/files', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fileKeys.all });
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-2550: the Smart tagging card's totals count these rows and its status query is cached for 60s, so any change to the set of files must refresh it.
      qc.invalidateQueries({ queryKey: knowledgeEnrichmentKeys.all });
      // FRONT-1605 review, finding M2: the knowledge catalogue's overview
      // (the Files-screen "Start here" panel) had no invalidation of its
      // own anywhere — a save/upload/delete left it reading a 60s-stale
      // snapshot with no way to know it should refetch sooner.
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: the overview grid combines uploads+documents and
      // pages server-side, so every mutation that used to just refresh
      // `fileKeys`/`documentKeys` must also invalidate its prefix.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
    },
    onError: (err) => toast.error(`Failed to create file: ${(err as Error).message}`),
  });
}

/**
 * FRONT-1841: `sessionId`/`scopeEntityId` are OPTIONAL and additive — every
 * existing caller (the phone composer, the file picker's replace flow) omits
 * both and uploads exactly as before. When the composer knows both (a chat
 * scoped to a Work container), the backend files the new upload onto that
 * container the same way it already does for a document Eddy saves or a
 * plain file `file_write` produces, and reports it back as `attachedTo`.
 */
export function useUploadFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; name?: string; description?: string; sessionId?: string; scopeEntityId?: string }) => {
      const fd = new FormData();
      fd.append('file', input.file);
      if (input.name) fd.append('name', input.name);
      if (input.description) fd.append('description', input.description);
      if (input.sessionId) fd.append('sessionId', input.sessionId);
      if (input.scopeEntityId) fd.append('scopeEntityId', input.scopeEntityId);
      return api.form<FileFull & { kind: FileMeta['kind']; attachedTo: AttachedToContainer | null }>('/api/files/upload', fd);
    },
    // FRONT-2741: a resend after a server error can store a second copy.
    // One attempt; the person decides whether to try again.
    retry: false,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: fileKeys.all });
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-2550: the Smart tagging card's totals count these rows and its status query is cached for 60s, so any change to the set of files must refresh it.
      qc.invalidateQueries({ queryKey: knowledgeEnrichmentKeys.all });
      // See useCreateFile's matching comment (FRONT-1605 review, finding M2).
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see useCreateFile's matching comment above.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      // FRONT-1841: same two keys `useCreateDocument`'s matching `attachedTo`
      // branch invalidates (use-document-mutations.ts) — the container's own
      // attachment list AND the board's `· N files` chip, or the project page
      // and board card go stale after an upload that just auto-filed.
      // `data?.` (not `data.`): `useUploadAndAttachFile`
      // (use-work-file-mutations.ts) composes this hook's `onSuccess` and is
      // exercised by a test that fires it directly with no arguments — see
      // that file's own test for why.
      if (data?.attachedTo) {
        qc.invalidateQueries({ queryKey: workFileKeys.byContainer(data.attachedTo.entityId) });
        qc.invalidateQueries({ queryKey: workBoardKeys.all });
        // FRONT-1841 adversarial-review fix: a dropped/pasted/picked file
        // has no Eddy turn to say "I filed that" the way a tool call does —
        // this toast is the equivalent. `resolveUploadFiledMessage` returns
        // `null` for the ordinary (unscoped) case, which never reaches this
        // branch anyway since `data.attachedTo` is only ever set when the
        // backend actually filed it — see that helper's own docblock
        // (`~/lib/document-save-outcome.ts`) for why it still returns a
        // message when the (best-effort, non-blocking) container-name
        // lookup came back blank.
        const message = resolveUploadFiledMessage(data.name, data.attachedTo);
        if (message) toast.success(message);
      }
    },
    onError: (err) => toast.error(`Upload failed: ${(err as Error).message}`),
  });
}

/**
 * Save an uploaded file. The body is PARTIAL by design (FRONT-1107): the route
 * only touches the fields it is given, so a caller that changed the content but
 * not the name must omit `name` rather than echo back whatever name it happens
 * to be displaying — echoing a stale one silently undoes a rename made
 * elsewhere.
 */
export function useUpdateFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<UpsertBody> & { id: string }) =>
      api.put<FileFull>(`/api/files/${encodeURIComponent(id)}`, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: fileKeys.list() });
      qc.invalidateQueries({ queryKey: fileKeys.detail(id) });
      // FRONT-1107: a file's name is also its inbox row's headline, so a rename
      // has to reach the feed too — otherwise the inbox keeps the old name for
      // up to its five-minute refetch interval. (Documents already did this via
      // their own update hook; the two kinds used to disagree.)
      qc.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-1605 review, finding M2: a content/metadata save can change
      // whether the file is stale or which OKF type it's teased under.
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see useCreateFile's matching comment above.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
    },
    onError: (err) => toast.error(`Failed to save file: ${(err as Error).message}`),
  });
}

/**
 * Rename an uploaded file inline (FRONT-590) — sends only the new name (the
 * file update accepts a partial body, so the content is left untouched). No
 * error toast here: the shared inline-rename control owns success + error
 * toasts so every surface behaves identically. Invalidates the file list so the
 * unified Files view picks up the new name.
 */
export function useRenameFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.put<FileFull>(`/api/files/${encodeURIComponent(id)}`, { name }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: fileKeys.list() });
      qc.invalidateQueries({ queryKey: fileKeys.detail(id) });
      // FRONT-1605 review, finding M2: a rename changes the title the
      // catalogue overview teases the item under.
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see useCreateFile's matching comment above.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
    },
  });
}

/** Move a single file to the recycle bin (soft delete, FRONT-594). */
export function useDeleteFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/files/${encodeURIComponent(id)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fileKeys.all });
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-2550: the Smart tagging card's totals count these rows and its status query is cached for 60s, so any change to the set of files must refresh it.
      qc.invalidateQueries({ queryKey: knowledgeEnrichmentKeys.all });
      // FRONT-1605 review, finding M2 — see useCreateFile's matching comment.
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see useCreateFile's matching comment above.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
    },
    onError: (err) => toast.error(`Failed to delete file: ${(err as Error).message}`),
  });
}

export type TrashAction = 'trash' | 'restore' | 'purge';

/**
 * Bulk recycle-bin action on uploaded files (FRONT-594): trash / restore /
 * purge a set of ids in one request. Invalidates both the live list and the
 * bin (fileKeys.all covers both).
 */
export function useBulkFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, ids }: { action: TrashAction; ids: string[] }) =>
      api.post<{ affected: number }>('/api/files/bulk', { action, ids }),
    onSuccess: (_, { action }) => {
      qc.invalidateQueries({ queryKey: fileKeys.all });
      // FRONT-2550: the Smart tagging card's totals count these rows and its status query is cached for 60s, so any change to the set of files must refresh it.
      qc.invalidateQueries({ queryKey: knowledgeEnrichmentKeys.all });
      // FRONT-776 code-review round 3, finding 10: only a PURGE cascades a
      // folder-member detach server-side (`detachPurgedFolderMembers`,
      // src/files/store.ts) — trash/restore leave folder membership
      // untouched. Without this, a folder's `· N files` chip and member
      // list keep showing a file that was just purged for good.
      if (action === 'purge') qc.invalidateQueries({ queryKey: folderKeys.all });
      // FRONT-1605 review, finding M2 — trash/restore/purge all change
      // what the catalogue overview should be counting/teasing.
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see useCreateFile's matching comment above.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      // FRONT-2062 F16: same reasoning as `useBulkDocuments`'s matching
      // fix in use-documents.ts — "Undo" on the delete toast from the
      // review screen calls this mutation directly, and a restored upload
      // must reappear in the stale-review list and knowledge search too.
      qc.invalidateQueries({ queryKey: knowledgeReviewKeys.lists() });
      // The sidebar's Knowledge badge counts the same queue (/api/attention).
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      qc.invalidateQueries({ queryKey: knowledgeSearchKeys.all });
    },
    onError: (err) => toast.error(`Action failed: ${(err as Error).message}`),
  });
}
